import {
    MemberCardDto,
    CoachRowDto,
    OrderDto,
    EvidenceResponse,
    EvidenceReasonDto,
} from '../../types/opsBriefing';
import {
    resolvePassOrder,
    resolveStage,
    lookupChurnRate,
    isFirstPass,
    CHURN_RATE_BY_ORDER,
    PassOrderResult,
    ChurnRateLookup,
    MemberStageKey,
} from './passOrder';
import { computeMemberValue, MemberValue } from './memberValue';

/**
 * 서버 응답(DTO) → 화면 모델.
 *
 * 서버는 원본만 줍니다. 순번·단계·이탈률 안내는 여기서 계산합니다.
 * 계산을 서버와 프론트 양쪽에 두면 반드시 갈리기 때문입니다.
 *
 * 이 파일이 얇을수록 좋습니다. 여기서 하는 일은 딱 하나 —
 * "원본에서 파생값을 붙이는 것"입니다. 값을 고치거나 만들어내지 않습니다.
 */

export type MemberView = MemberCardDto & {
    passOrder: PassOrderResult;
    stage: MemberStageKey;
    churnHint: ChurnRateLookup;
    /** 실제 결제 정보로 계산한 금액. 단가를 모르면 null */
    value: MemberValue | null;
    /**
     * 이 **카드** 한 장의 식별자.
     *
     * ─────────────────────────────────────────────────────────────
     * ⚠️ `memberId` 는 카드의 식별자가 아닙니다. 회원의 식별자입니다.
     * ─────────────────────────────────────────────────────────────
     * 서버는 회원 한 명당 카드 한 장이 아니라 **활성 수강권 한 건당 카드 한 장**을
     * 줍니다 (rappo `opsBriefing.service.toMemberCard` 주석: "회원 한 명이 활성
     * 수강권을 둘 이상 갖고 있으면 카드도 둘"). 그래서 같은 `memberId` 가 여러 번
     * 옵니다.
     *
     * 그걸 모르고 목록은 `key={m.memberId}`, 상세는 `find(m => m.memberId === openId)`
     * 로 잡고 있었습니다. 결과는 **원장이 누른 줄과 다른 줄이 열리는 것**이었습니다 —
     * 0원짜리 이관 수강권 행(「금액 산정 불가」)을 눌렀는데 드로어에는 그 회원의
     * 다른 수강권 금액(1,321,320원)이 떴습니다. 표시만 틀린 것이 아니라, 드로어의
     * 지시 버튼이 **다른 수강권의 금액을 지시에 실어 보냈습니다.**
     *
     * ─────────────────────────────────────────────────────────────
     * 왜 등장 순번이 아니라 내용으로 만드나
     * ─────────────────────────────────────────────────────────────
     * DTO 에 `membershipId` 가 없어 서버가 준 진짜 식별자를 쓸 수 없습니다
     * (백엔드에 요청해 둠 — BACKEND_HANDOFF_OPS_BRIEFING_MEMBER_CARD_ID.md).
     * 그렇다고 `memberId#0`, `memberId#1` 처럼 **배열 순서**로 만들면 안 됩니다.
     * 서버 조회에 정렬이 없어서, 다시 받아오면 `#0` 이 다른 수강권으로 바뀔 수
     * 있습니다. 그러면 열려 있던 드로어가 **조용히 다른 수강권으로 갈아탑니다** —
     * 지금 고치려는 바로 그 버그가 다른 얼굴로 돌아옵니다.
     *
     * 그래서 그 수강권을 설명하는 값으로 만듭니다. 잔여 회차는 **일부러 뺐습니다** —
     * 수업 한 번만 해도 값이 바뀌어 열려 있던 드로어가 닫힙니다.
     * 그래도 겹치면(상품·회차·단가가 똑같은 활성 수강권 두 건) 등장 순번을 덧붙입니다.
     * 그 경우 두 카드는 금액이 같으므로 어느 쪽이 열려도 숫자가 갈리지 않습니다.
     *
     * 못 찾으면 드로어는 그냥 닫힙니다(`find` 가 undefined). 엉뚱한 카드를 여는
     * 것보다 낫습니다.
     */
    cardKey: string;
};

/**
 * 이 단계에서 재등록을 기대해도 되는가 — 정책 M-D2.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 `valueKindOf` 를 대체합니다. 단계는 이제 **성격을 가르지 않습니다**
 * ─────────────────────────────────────────────────────────────
 * 예전에는 이 함수가 `GOLDEN|STABLE → GAIN, 그 외 → DEFEND` 로 **돈의 성격**을
 * 갈랐습니다. 그래서 골든타임·안정 회원의 잔여 선수금이 어느 합계에도 안 잡혔고
 * (§2-4), 이탈 위험 회원에게는 재등록률이 곱해졌습니다(§2-1).
 *
 * 이제 잔여 선수금은 **단계와 무관하게 전 회원**이 갖고, 단계는 딱 하나만
 * 정합니다 — **재등록을 기대하는 자리인가.**
 *
 * 이탈 위험(AT_RISK)·주의(WATCH)는 아닙니다. 위험은 "이미 떠나는 중"이고
 * 주의는 "안심할 단계가 아니다"입니다. 그 자리에서 재등록률을 곱하면 화면이
 * 같은 카드에서 두 가지 반대말을 합니다. 첫 수강권(FIRST_PASS)은 **기대합니다** —
 * 이탈률이 가장 높은 구간이지만 그게 곧 재등록 영업 대상이라는 뜻이고,
 * 순번별 요율(52.9%)이 이미 그 위험을 반영한 값입니다.
 *
 * ⚠️ 브리핑 항목 쪽은 단계를 계산할 원본(pace 등)이 없어 **등급**으로 같은 판정을
 *    합니다 (`briefingValue.expectsReRegistrationByGrade`). 둘이 갈리면 같은 회원이
 *    두 화면에서 다른 기대를 받습니다 — 한쪽을 고치면 반드시 다른 쪽도 보세요.
 */
export const expectsReRegistration = (stage: MemberStageKey): boolean =>
    stage !== 'AT_RISK' && stage !== 'WATCH';

/**
 * ⚠️ 카드 한 장만 바꿉니다. `cardKey` 는 목록 전체를 봐야 정해지므로
 *    여기서 채우지 않고 `toMemberViews` 가 채웁니다. 한 장씩 부르는 곳
 *    (테스트 등)에서는 빈 문자열이라 목록 식별에 쓰면 안 됩니다.
 */
export const toMemberView = (dto: MemberCardDto): MemberView => {
    /*
     * ─────────────────────────────────────────────────────────────
     * 🔴 **이 카드 자신의 수강권 순번**을 구합니다 (2026-08-31, 대표 승인)
     * ─────────────────────────────────────────────────────────────
     * 예전에는 `resolvePassOrder(dto.memberships)` 였습니다 — targetId 없이 부르면
     * 함수는 **가장 최근 수강권**의 순번을 돌려줍니다(passOrder.ts 의 기본값).
     *
     * 카드는 회원이 아니라 **수강권 한 건**입니다. 그래서 수강권을 둘 가진 회원의
     * 두 줄이 **똑같이 「2번째」**로 찍혔습니다. 1월에 산 첫 수강권 줄에도
     * 「2번째」라고 적혀 있었고, 그 줄은 2번째 요율(53.4%)로 금액을 냈습니다.
     *
     * 실서버 279장 중 **88장이 틀린 순번**이었고, 그중 24장은 단계까지 달랐습니다
     * (전부 「첫 수강권」으로 들어갑니다 — 실제로 그 회원의 1번째 수강권입니다).
     *
     * ⚠️ `membershipId` 가 없으면(구버전 서버·목·테스트) 예전처럼 최신 순번으로
     *    떨어집니다. `resolvePassOrder` 의 두 번째 인자가 `undefined` 면 기본
     *    동작이라, 프론트가 서버보다 먼저 배포돼도 화면이 안 깨집니다.
     *
     * ⚠️ `membershipId` 가 있는데 순번 기록에 없으면(양도·전환으로 받은 수강권)
     *    함수가 `basis: 'NONE'` 을 돌려줍니다. 그게 맞습니다 — 그 수강권은 순번을
     *    셀 대상이 아닙니다. 예전에는 남의 순번을 빌려 쓰고 있었습니다.
     *    실서버에서 1장이 여기 해당합니다.
     */
    const passOrder = resolvePassOrder(dto.memberships, dto.membershipId);

    /*
     * ─────────────────────────────────────────────────────────────
     * 🔴 **단계 판정은 회원 순번으로** 합니다 — 카드 순번이 아닙니다
     * ─────────────────────────────────────────────────────────────
     * 두 값이 묻는 질문이 다릅니다.
     *
     *   `passOrder`   **카드의 사실** — 이 수강권이 그 회원의 몇 번째인가.
     *                 라벨(「1번째」)·요율·이탈률 표시가 이걸 씁니다.
     *   `memberOrder` **회원의 국면** — 이 회원이 아직 첫 수강권 구간인가.
     *                 단계 판정(FIRST_PASS 게이트)만 이걸 씁니다.
     *
     * 수강권 2건인 회원의 **옛 1번째 카드**에서 둘이 갈립니다. 그 카드의 순번은
     * 정말 1번째지만, 그 회원은 **이미 재등록했습니다.** 카드 순번으로 단계를
     * 정하면 이미 다음 수강권을 산 사람이 「첫 수강권 · 이탈률 가장 높은 구간」에
     * 섭니다 — 실측 47 → 71장(+51%).
     *
     * 예전 코드(targetId 없이 한 번만 부르던 시절)는 **우연히 회원 국면은 맞고
     * 카드 순번은 틀렸습니다.** 이번에 둘을 갈라서 **둘 다** 맞게 합니다.
     *
     * 검증(2026-08-31 실서버 279장): 단계 분포가 순번 수정 **전과 정확히 동일**하고
     * (인원·카드·선수금 전 단계 일치), 달라진 것은 카드별 요율뿐입니다.
     */
    const memberOrder = resolvePassOrder(dto.memberships);

    /*
     * lessonCount 를 반드시 넘겨야 합니다.
     * 안 넘기면 "비교 기준은 없지만 3주째 안 나오는 회원"이 여기서는 안전으로,
     * 서버 배치에서는 이탈 위험으로 갈립니다. 두 화면의 명단이 달라집니다.
     */
    /*
     * `...dto` 로 넘기므로 `totalSession`·`sessionless` 가 그대로 따라갑니다 —
     * 무제한(대관) 수강권을 잔여 회차로 골든타임 판정하지 않게 하는 값입니다
     * (passOrder.resolveStage 의 lowRemain 주석).
     */
    const stage = resolveStage(
        { ...dto, lessonCount: dto.paceNow + dto.paceBefore },
        memberOrder
    );
    return {
        ...dto,
        passOrder,
        stage,
        /* 목록 전체를 봐야 정해진다 — toMemberViews 가 덮어쓴다 */
        cardKey: '',
        churnHint: lookupChurnRate(passOrder),
        value: computeMemberValue(
            {
                remain: dto.remain,
                pricePerSchedule: dto.pricePerSchedule,
                totalPrice: dto.totalPrice,
                totalSession: dto.totalSession,
                /*
                 * 무제한 판정에 쓰는 값. 빠뜨리면 `999 × 단가` 가 그대로
                 * 「지켜야 할 매출」에 들어갑니다 (sessionless.ts 주석).
                 */
                sessionless: dto.sessionless,
            },
            passOrder,
            { expectsReRegistration: expectsReRegistration(stage) }
        ),
    };
};

/** 이 카드가 어느 수강권인가 — 잔여 회차는 뺀다 (수업 한 번에 바뀌면 안 된다) */
const cardShape = (dto: MemberCardDto): string =>
    [dto.memberId, dto.product, dto.totalSession, dto.pricePerSchedule ?? '', dto.totalPrice ?? ''].join(
        '\u0000'
    );

export const toMemberViews = (list: MemberCardDto[]): MemberView[] => {
    /*
     * 같은 모양의 카드가 두 장 이상이면 등장 순번으로 가른다.
     * 그런 카드끼리는 금액이 같으므로 어느 쪽이 열려도 숫자가 갈리지 않는다.
     */
    const seen = new Map<string, number>();
    return list.map((dto) => {
        const shape = cardShape(dto);
        const n = seen.get(shape) ?? 0;
        seen.set(shape, n + 1);
        return { ...toMemberView(dto), cardKey: n === 0 ? shape : `${shape}\u0000${n}` };
    });
};

/* ═══════════════════════════════════════════════════════════
   합계 — 정책 M2 · M4 · M7
   ═══════════════════════════════════════════════════════════ */

type Valued = { value?: { prepaid: number | null; expected: number | null } | null };

/**
 * 💰 지켜야 할 매출 = **Σ 미사용 선수금** (정책 M2).
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 그냥 더해도 되나 — 잔여 합산은 수강권 단위와 회원 단위가 같습니다
 * ─────────────────────────────────────────────────────────────
 * 선수금은 수강권마다 **따로 받은 돈**입니다. 같은 회원이 20회권과 10회권을
 * 갖고 있으면 두 결제가 실제로 두 번 있었고, 남은 회차도 각각 남아 있습니다.
 * 그래서 카드별로 더한 값이 곧 회원별로 더한 값입니다 — 이중계상이 아닙니다.
 *
 * 🔴 **재등록 기대는 다릅니다.** 그건 "이 회원이 다음에 한 번 더 산다"는 기댓값이라
 *    수강권 수만큼 곱하면 없는 매출이 만들어집니다 (`sumExpected` 참고).
 *
 * `null`(모르는 건)은 0 으로 더하지 않고 뺍니다. 합계는 같아 보여도
 * 「챙길 게 없는 건」으로 묻힙니다 — `countUnpriced` 가 그 수를 따로 셉니다.
 */
export const sumPrepaid = (items: Valued[]): number =>
    items.reduce((sum, i) => sum + (i.value?.prepaid ?? 0), 0);

/**
 * 📈 확보 가능한 매출 = **Σ 재등록 기대** (정책 M4).
 *
 * 🔴 **회원당 한 번만 셉니다** (정책 M7). 부르는 쪽이 회원별 대표 카드 한 장만
 *    남겨서 넘겨야 합니다 — 회원 현황은 `pickPrimaryCards`, 오늘의 브리핑은
 *    `dedupeBySubject` 를 씁니다. 목록을 그대로 넘기면 수강권이 셋인 회원의
 *    다음 결제가 **세 번** 기대되어 「확보 가능한 매출」이 부풀어 오릅니다.
 */
export const sumExpected = (items: Valued[]): number =>
    items.reduce((sum, i) => sum + (i.value?.expected ?? 0), 0);

export const countUnpriced = (items: Array<{ value?: unknown }>): number =>
    items.filter((i) => i.value == null).length;

/**
 * 회원마다 **대표 수강권 카드 한 장**만 남긴다 — 재등록 기대를 한 번만 세기 위해.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 「첫 번째 카드」로 하면 안 되나
 * ─────────────────────────────────────────────────────────────
 * 서버 조회에 정렬이 없습니다(`cardKey` 주석). 다시 받아오면 배열 순서가 바뀔 수
 * 있고, 그러면 같은 데이터인데 새로고침할 때마다 「확보 가능한 매출」이 달라집니다.
 * 원장님이 F5 를 눌렀더니 매출 예상이 바뀌는 화면은 한 번 보면 다시 안 믿습니다.
 *
 * 그래서 **서버가 정한 대표 수강권**(`memberRollup.primaryMembershipId`)을 씁니다.
 * 그게 표시·지시가 걸리는 바로 그 한 건이라 화면의 다른 자리와도 어긋나지 않습니다.
 *
 * 폴백(대표를 못 찾을 때 — 구버전 서버·목·테스트)도 **배열 순서를 보지 않습니다.**
 * 기대가 가장 큰 카드를 고르고, 같으면 `cardKey` 사전순으로 가릅니다.
 * 임의로 고른 것이 아니라 「그 회원이 다음에 살 가능성이 가장 큰 수강권」이고,
 * 무엇보다 **같은 입력이면 항상 같은 답**입니다.
 */
export const pickPrimaryCards = <T extends MemberView>(list: T[]): T[] => {
    const byMember = new Map<string, T[]>();
    list.forEach((m) => {
        const bucket = byMember.get(m.memberId);
        if (bucket) bucket.push(m);
        else byMember.set(m.memberId, [m]);
    });

    const out: T[] = [];
    byMember.forEach((cards) => {
        const primary = cards.find(
            (m) =>
                m.membershipId != null &&
                m.memberRollup?.primaryMembershipId != null &&
                m.membershipId === m.memberRollup.primaryMembershipId
        );
        if (primary) {
            out.push(primary);
            return;
        }
        out.push(
            [...cards].sort((a, b) => {
                const ea = a.value?.expected ?? 0;
                const eb = b.value?.expected ?? 0;
                if (ea !== eb) return eb - ea;
                return a.cardKey < b.cardKey ? -1 : a.cardKey > b.cardKey ? 1 : 0;
            })[0]
        );
    });
    return out;
};

/* ═══════════════════════════════════════════════════════════
   브리핑 항목의 수강권 순번
   ═══════════════════════════════════════════════════════════ */

/**
 * 브리핑 항목의 순번 — **원본이 없으면 `null`.**
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 "모른다"를 따로 두나
 * ─────────────────────────────────────────────────────────────
 * 서버가 `payload.membership.memberships[]` 를 보내기 시작하기 전에는
 * 이 값이 `null` 입니다. 그때 화면은 **가중도 문구도 그냥 생략합니다.**
 *
 * `resolvePassOrder([])` 를 대신 부르면 안 됩니다. 그건 「셀 수 있는 수강권이
 * 없다(NONE)」라는 **다른 사실**이고, 화면이 그걸 "순번 없음"이라고 적기
 * 시작하면 서버가 아직 안 보내는 것과 양도·전환으로만 받은 회원이 같아집니다.
 * 모르는 것은 말하지 않습니다 (briefingValue.ts 의 같은 규칙).
 */
export const itemPassOrder = (item: {
    membership?: {
        membershipId?: string;
        memberships?: MemberCardDto['memberships'];
    } | null;
}): PassOrderResult | null => {
    const records = item.membership?.memberships;
    if (!records || records.length === 0) return null;
    /*
     * 🔴 **그 항목이 걸린 수강권의 순번**입니다 (2026-08-31, 회원 현황과 같은 수정).
     *
     * 브리핑 항목은 회원이 아니라 **수강권 한 건**을 가리킵니다
     * (`payload.membership.membershipId`). targetId 없이 부르면 회원의 **최신**
     * 수강권 순번이 나와서, 브리핑에 올라온 것이 옛 수강권일 때 엉뚱한 요율로
     * 금액이 계산됩니다.
     *
     * 여기를 안 고치면 **같은 수강권이 두 화면에서 다른 순번**을 받습니다 —
     * 회원 현황 배지는 「1번째」인데 오늘의 브리핑 금액은 2번째 요율(53.4%)로
     * 나옵니다. 이 함수를 둘이 공유하는 이유가 정확히 그것을 막기 위해서입니다.
     *
     * 서버가 `membershipId` 를 안 보내면 `undefined` 라 예전 동작(최신 순번)입니다.
     */
    return resolvePassOrder(records, item.membership?.membershipId);
};

/**
 * 이 브리핑 항목이 첫 수강권 **회원**인가 — **확정된 경우만 true.**
 *
 * `isFirstPass` 가 `basis === 'EXACT'` 만 받으므로 재등록으로 들어온 회원
 * (AT_LEAST)과 분류가 안 된 회원(UNCLASSIFIED)은 여기서 빠집니다.
 * 순번이 하한선일 뿐인 회원을 첫 수강권 자리에 세우면, 이미 여러 번 끊은
 * 회원이 「첫 수강권」이라고 적힌 카드로 원장님 앞에 섭니다.
 *
 * 우선순위 가중과 카드 문구가 **둘 다 이 함수를** 봅니다 — 갈리면
 * 「첫 수강권」이라고 적히지 않은 카드가 첫 수강권이라는 이유로 위에 서게 됩니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 **회원 기준입니다 — `itemPassOrder`(카드 기준)를 쓰지 마세요**
 * ─────────────────────────────────────────────────────────────
 * 이건 「이 회원이 첫 수강권 국면인가」를 묻는 자리입니다. 회원 현황의 단계
 * 게이트와 같은 질문이고(`passOrder.resolveStage` 의 `memberOrder` 주석),
 * 답도 같아야 합니다 — 안 그러면 같은 회원이 오늘의 브리핑에서는 첫 수강권,
 * 회원 현황에서는 안정 궤도가 됩니다.
 *
 * **서버 판정과도 이래야 맞습니다.** 서버는 `isFirstPassRecords(records)` 로
 * targetId 없이 셉니다 — 즉 **최신 수강권 기준**입니다
 * (rappo `opsPassOrder.service.isFirstPassRecords` → `resolvePassOrder(records)`).
 * 이 폴백은 서버가 키를 안 보내는 옛 문서에서만 쓰이므로, 서버와 같은 식이어야
 * 옛 문서와 새 문서의 배지가 어긋나지 않습니다.
 *
 * ⚠️ `itemPassOrder` 는 **금액 요율**용입니다(그 항목이 걸린 수강권의 순번).
 *    둘을 같은 함수로 묶지 마세요 — 묻는 질문이 다릅니다.
 */
export const isFirstPassItem = (item: {
    membership?: {
        /* 받기는 하지만 **일부러 안 씁니다** — 위 주석의 「회원 기준」이 그 이유입니다 */
        membershipId?: string;
        memberships?: MemberCardDto['memberships'];
    } | null;
}): boolean => {
    const records = item.membership?.memberships;
    if (!records || records.length === 0) return false;
    const memberOrder = resolvePassOrder(records);
    return isFirstPass(memberOrder);
};

/**
 * 🔴 이 항목이 첫 수강권인가 — **서버 값이 있으면 서버 값.**
 *
 * ─────────────────────────────────────────────────────────────
 * 2026-08-27 백엔드 3.5 — 판정이 서버로 넘어갔습니다
 * ─────────────────────────────────────────────────────────────
 * 서버가 항목을 만들 때 순번을 세어 `isFirstPass` 를 **박제해** 보냅니다.
 * 그 값으로 서버가 등급 안의 순서까지 이미 정해 둡니다.
 *
 * 웹이 자기 계산을 고집하면 두 가지가 어긋납니다.
 *   ① **박제 시점이 다릅니다.** 서버 값은 그날 아침 기준이고, 웹 계산은
 *      지금 응답에 실린 `memberships[]` 기준입니다. 그 사이 수강권이 하나
 *      더 등록되면 같은 카드가 서버에서는 첫 수강권, 웹에서는 아닙니다.
 *   ② **가중과 배지가 갈립니다.** 갈리면 「첫 수강권」이라고 **안 적힌** 카드가
 *      첫 수강권이라는 이유로 위에 섭니다 — 원장님이 읽기에 그건 고장입니다.
 *
 * 그래서 **정렬과 배지가 둘 다 이 함수 하나를** 봅니다.
 *
 * ⚠️ 폴백은 그대로 둡니다. 옛 문서(v7 이하)에는 이 키가 없고, 지난 리포트를
 *    열면 그런 문서가 나옵니다. 키가 없을 때 false 로 떨어뜨리면 **과거의
 *    첫 수강권 표시가 소급해서 사라집니다.**
 *
 * ⚠️ 순번 라벨(「1번째」)·이탈률 문구는 여기서 안 만듭니다. 서버가 주는 것은
 *    **불리언 하나**뿐이고, 라벨은 `memberships[]` 로 웹이 계속 계산합니다
 *    (`itemPassOrder`). 서버가 라벨까지 만들면 회원 현황의 순번 배지와 갈립니다.
 */
export const firstPassOfItem = (item: {
    isFirstPass?: boolean;
    membership?: { memberships?: MemberCardDto['memberships'] } | null;
}): boolean => (item.isFirstPass != null ? item.isFirstPass : isFirstPassItem(item));

/**
 * 이 목록이 **서버가 순서를 정한 것**인가 (생성기 v8 이상).
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 「하나라도 있으면」으로 보나
 * ─────────────────────────────────────────────────────────────
 * `isFirstPass` 는 v8 부터 **모든 회원 항목에** 실립니다. 그래서 한 문서 안에서
 * 섞이지 않습니다 — 하나만 봐도 그 문서의 세대를 알 수 있습니다.
 *
 * 「전부 있으면」으로 판정하면 안 됩니다. 강사·센터 항목에는 이 키가 없어서
 * (회원이 아니라 순번 자체가 없습니다) v8 문서인데도 false 가 나옵니다.
 *
 * 값이 `false` 여도 **키가 있으면 v8** 입니다. 「첫 수강권이 아니다」와
 * 「모른다」는 다른 말이라 `!= null` 로 봅니다.
 */
export const hasServerFirstPass = (items: Array<{ isFirstPass?: boolean }>): boolean =>
    items.some((i) => i.isFirstPass != null);

/* ═══════════════════════════════════════════════════════════
   원천 데이터 — 순번 근거는 여기서 붙인다
   ═══════════════════════════════════════════════════════════ */

/**
 * 수강권 순번 근거.
 *
 * 서버는 원본(memberships)만 보냅니다. 순번 판정과 이탈률 적용은
 * passOrder.ts 한 곳에서만 합니다 — 서버가 같이 계산하면
 * 회원 현황의 순번 뱃지와 이 근거의 순번이 갈립니다.
 *
 * 순번을 확정할 수 없으면 근거를 만들지 않습니다.
 * "몇 번째인지 모르겠지만 위험합니다"는 근거가 아닙니다.
 */
export const passOrderReason = (
    memberships: MemberCardDto['memberships'] | undefined
): EvidenceReasonDto | null => {
    if (!memberships || !memberships.length) return null;

    const order = resolvePassOrder(memberships);
    const churn = lookupChurnRate(order);
    if (churn.rate == null) return null;

    const others = Object.entries(CHURN_RATE_BY_ORDER)
        .map(([n, rate]) => ({ n: Number(n), rate }))
        .filter((x) => x.n !== order.order)
        .map((x) => ({
            label: `${x.n}번째 수강권`,
            value: x.rate,
            // 이탈률이 가장 낮은 구간을 초록으로 — 어디로 가야 하는지가 보인다
            tone: x.rate === Math.min(...Object.values(CHURN_RATE_BY_ORDER)) ? ('GOOD' as const) : undefined,
        }));

    return {
        kind: 'SEGMENT',
        // 순번이 하한선이면(재등록 유입) 확정값처럼 쓰면 안 된다
        confidence: order.isAtLeast ? 'WEAK' : 'VERIFIED',
        title: isFirstPass(order)
            ? '첫 수강권은 원래 가장 많이 빠져나가는 구간입니다'
            : `${order.label} 구간의 이탈률입니다`,
        plain: `${order.reason} ${churn.text}`,
        compare: {
            unit: '%',
            mine: { label: `${order.label} (이 회원)`, value: churn.rate },
            others,
        },
    };
};

/**
 * 서버 근거 응답 → 화면 모델.
 * 순번 근거를 맨 앞 행동 신호 다음에 끼워 넣습니다 —
 * "이 회원이 실제로 보인 변화"가 먼저, "구간 자체의 위험"이 그다음입니다.
 */
export const toEvidenceView = (dto: EvidenceResponse): EvidenceResponse => {
    const segment = passOrderReason(dto.memberships);
    if (!segment) return dto;
    // 서버가 이미 SEGMENT 근거를 보냈다면 중복으로 넣지 않는다
    if (dto.reasons.some((r) => r.kind === 'SEGMENT')) return dto;

    const signalCount = dto.reasons.filter((r) => r.kind === 'SIGNAL').length;
    const reasons = [
        ...dto.reasons.slice(0, signalCount),
        segment,
        ...dto.reasons.slice(signalCount),
    ];
    return { ...dto, reasons };
};

/**
 * 강사 응답은 그대로 씁니다.
 * 분석(강점·약점)은 coachAnalysis 가 별도로 하므로 여기서 손댈 게 없습니다.
 */
export type CoachView = CoachRowDto;

/**
 * 지시 응답 → 화면 모델.
 *
 * 서버는 ISO 시각을 주고 화면은 "어제 09:14"처럼 읽습니다.
 * 매번 포맷하면 목록이 스크롤될 때마다 다시 계산되므로 여기서 한 번만 만듭니다.
 */
export type OrderView = OrderDto & {
    sentLabel: string;
    seenLabel?: string;
    doneLabel?: string;
    /**
     * 회원에게 건넬 문구. 화면은 '있다/없다' 둘로만 봅니다.
     *
     * 서버가 키를 안 보내는 경우(프론트 선배포)와 null 을 같게 눕혀 둡니다.
     * 여기서 한 번 눕혀 놓으면 화면마다 `?? null` 을 다시 쓰지 않아도 됩니다.
     */
    memberDraft: string | null;
    /**
     * 언제 철회했나 — "오늘 15:30".
     *
     * ⚠️ `sentLabel` 로 대신할 수 없습니다. 그건 **보낸 날** 기준이라
     *    60일 전 지시를 방금 철회해도 "60일 전"이라고 말합니다. 철회 카드가
     *    답해야 할 질문은 "언제 보낸 지시였나"가 아니라 "방금 뭐가 취소됐나"입니다.
     */
    retractedLabel?: string;
    /** 강사 알림함에 기록된 시각 — "오늘 15:32". 전달되지 않았으면 없습니다 */
    retractDeliveredLabel?: string;
    /** 철회 사유. 없으면 null (회수 갈래는 사유가 선택이라 대개 없습니다) */
    retractNote: string | null;
};

/** 며칠 전인지를 사람 말로. 사흘이 넘으면 날짜를 그대로 보여준다 */
const relativeDay = (offsetDays: number): string => {
    if (offsetDays <= 0) return '오늘';
    if (offsetDays === 1) return '어제';
    if (offsetDays === 2) return '그저께';
    return `${offsetDays}일 전`;
};

const hhmm = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** 이 시각이 며칠 전인가 (달력 기준). 못 읽으면 null */
const daysAgoOf = (iso: string): number | null => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    return Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
};

/**
 * 서버가 이미 사람이 읽는 문자열을 준 경우(목 데이터)는 그대로 쓰고,
 * ISO 시각이면 변환합니다. 서버 전환기에 두 형태가 섞여도 화면이 안 깨집니다.
 *
 * 발송 시각만 서버가 sentDayOffset(KST 기준)을 함께 줍니다. 그건 그대로 씁니다 —
 * 브라우저 시간대가 KST 가 아니면 클라이언트 계산과 하루가 어긋납니다.
 * 확인·완료 시각은 서버가 오프셋을 주지 않아 여기서 셉니다.
 */
const timeLabel = (raw: string | undefined, offsetDays: number): string | undefined => {
    if (!raw) return undefined;
    const t = hhmm(raw);
    if (!t) return raw;
    return `${relativeDay(offsetDays)} ${t}`;
};

/**
 * 발송 이후에 찍힌 시각(확인·완료).
 * 예전에는 완료 시각을 무조건 '오늘 08:12' 로 찍었습니다 —
 * 사흘 전에 끝난 지시도 오늘 끝난 것처럼 보였습니다.
 */
const stampLabel = (raw: string | undefined): string | undefined => {
    if (!raw) return undefined;
    const t = hhmm(raw);
    if (!t) return raw;
    const ago = daysAgoOf(raw);
    return ago == null ? t : `${relativeDay(ago)} ${t}`;
};

export const toOrderView = (dto: OrderDto): OrderView => ({
    ...dto,
    // 공백만 있는 문구는 글이 아니다 — 서버 저장 규칙과 같게 본다
    memberDraft: dto.memberDraft?.trim() ? dto.memberDraft : null,
    sentLabel: timeLabel(dto.sentAt, dto.sentDayOffset) ?? '',
    seenLabel: stampLabel(dto.seenAt),
    doneLabel: stampLabel(dto.doneAt),
    /*
     * 철회 시각은 sentDayOffset 이 아니라 retractedAt 으로 셉니다.
     * 서버가 ISO 로 주는 것도 그래서입니다 — 철회는 방금 일어나는 일이라
     * 화면이 "오늘 15:30"까지 말할 수 있어야 합니다.
     */
    retractedLabel: stampLabel(dto.retractedAt ?? undefined),
    retractDeliveredLabel: stampLabel(dto.retractDeliveredAt ?? undefined),
    // 공백만 있는 사유는 글이 아니다 — memberDraft 와 같은 규칙
    retractNote: dto.retractNote?.trim() ? dto.retractNote : null,
});

export const toOrderViews = (list: OrderDto[]): OrderView[] => list.map(toOrderView);
